import type { ScoreCondition, ScoreRoute } from '../storyAssetTypes'

import { scoreKey } from './scores'

type Scores = Readonly<Record<string, number>>

const OPERATOR_SYMBOL: Record<ScoreCondition['operator'], string> = {
  gte: '≥',
  lte: '≤',
  gt: '>',
  lt: '<',
  eq: '=',
}

const scoreValue = (scores: Scores, condition: ScoreCondition): number =>
  scores[scoreKey(condition.scoreName, condition.characterId)] ?? 0

export const evaluateScoreCondition = (condition: ScoreCondition, scores: Scores): boolean => {
  const value = scoreValue(scores, condition)
  switch (condition.operator) {
    case 'gte': return value >= condition.value
    case 'lte': return value <= condition.value
    case 'gt': return value > condition.value
    case 'lt': return value < condition.value
    case 'eq': return value === condition.value
  }
}

const isScoreRoute = (value: string | ScoreRoute): value is ScoreRoute =>
  typeof value !== 'string'

/** Resolve one exit-route value to a target node id, evaluating score branches first-match-wins. */
export const resolveExitRouteTarget = (value: string | ScoreRoute, scores: Scores): string => {
  if (!isScoreRoute(value)) {
    return value
  }
  for (const branch of value.branches) {
    if (branch.conditions.every(condition => evaluateScoreCondition(condition, scores))) {
      return branch.targetNodeId
    }
  }
  return value.defaultNodeId
}

/** Every node a route can lead to (unique, non-empty). For graph traversal/validation. */
export const exitRouteTargets = (value: string | ScoreRoute): string[] => {
  const targets = isScoreRoute(value)
    ? [...value.branches.map(branch => branch.targetNodeId), value.defaultNodeId]
    : [value]
  return [...new Set(targets.filter(Boolean))]
}

/**
 * Resolve the next node id for an outcome, preserving the legacy `||` fallback:
 * empty/missing outcome route → `default` route → undefined (story ends).
 */
export const resolveNextNodeId = (
  exitRoutes: Record<string, string | ScoreRoute> | undefined,
  outcomeId: string,
  scores: Scores,
): string | undefined => {
  if (!exitRoutes) {
    return undefined
  }
  const direct = exitRoutes[outcomeId]
  const resolvedDirect = direct !== undefined ? resolveExitRouteTarget(direct, scores) : ''
  if (resolvedDirect) {
    return resolvedDirect
  }
  const fallback = exitRoutes.default
  const resolvedFallback = fallback !== undefined ? resolveExitRouteTarget(fallback, scores) : ''
  return resolvedFallback || undefined
}

const formatCondition = (condition: ScoreCondition): string => {
  const name = condition.characterId ? `${condition.characterId}:${condition.scoreName}` : condition.scoreName
  return `${name} ${OPERATOR_SYMBOL[condition.operator]} ${condition.value}`
}

/** Human-readable per-branch summary used by the graph edges and the editor inspector. */
export const summariseScoreRoute = (
  route: ScoreRoute,
): Array<{ label: string; targetNodeId: string; isDefault: boolean }> => [
  ...route.branches.map(branch => ({
    label: branch.conditions.map(formatCondition).join(' ∧ ') || '(always)',
    targetNodeId: branch.targetNodeId,
    isDefault: false,
  })),
  { label: 'default', targetNodeId: route.defaultNodeId, isDefault: true },
]
